import { DestroyRef, Injectable, inject } from "@angular/core";
import { HttpResponse } from "@angular/common/http";

import {
    Observable,
    filter,
    firstValueFrom
} from "rxjs";

import { FHIRMiddlewareApiService, LockResponseData } from "@fhir-api";

import { AppEtagManagerService } from "../app-etag-manager.service";
import { AppStorageService } from "../app-storage.service";
import { AppProjectsService } from "../app-projects.service";
import { AppStateService } from "./app-state.service";

@Injectable({ providedIn: "root" })
export class AppSubjectLockingManagerService {
    private _appStateService = inject(AppStateService);
    private _appEtagManagerService = inject(AppEtagManagerService);
    private _appStorageService = inject(AppStorageService);
    private _middlewareApiService = inject(FHIRMiddlewareApiService);
    private _projectsService = inject(AppProjectsService);
    private _project = this._appStateService.selectedRegistryName;
    private _organization = this._appStateService.selectedOrganization;
    private _rootDataset = this._projectsService.rootDataset;

    private _destroyRef = inject(DestroyRef);

    constructor() {
        this._destroyRef.onDestroy(async () => {
            await this.unlockSubject();
        });
    }

    isLocked(subjectName: string): boolean {
        return this._appStorageService.getFromSessionStorage("lockedSubjectName") === subjectName;
    }

    async unlock(): Promise<void> {
        await this.unlockSubject();
    }

    async unlockSubject(): Promise<void> {
        const subjectName = this._appStorageService.getFromSessionStorage("lockedSubjectName");

        if (!subjectName) {
            return;
        }

        this._appStorageService.removeFromSessionStorage("lockedSubjectName");

        const getValidEtag = this._appEtagManagerService.etag$.pipe(filter(etag => !!etag));
        const etag = await firstValueFrom(getValidEtag);

        this._appEtagManagerService.clear();
        await firstValueFrom(this.unlock$(subjectName, etag));
    }

    lock$(subjectUri: string, etag: string): Observable<HttpResponse<LockResponseData>> {
        if (!this._project()) {
            throw new Error("Project is not available");
        }
        if (!this._organization()) {
            throw new Error("Organization is not available");
        }
        return this._middlewareApiService.lock$(
            this._organization()!,
            this._project()!,
            this._rootDataset()!,
            subjectUri,
            etag
        );
    }

    unlock$(subjectUri: string, etag: string): Observable<HttpResponse<void>> {
        if (!this._project()) {
            throw new Error("Project is not available");
        }
        if (!this._organization()) {
            throw new Error("Organization is not available");
        }
        return this._middlewareApiService.unlock$(
            this._organization()!,
            this._project()!,
            this._rootDataset()!,
            subjectUri,
            etag
        );
    }
}
